import { Card, ConfigProvider, Typography, Flex, List } from 'antd';
import PropTypes from 'prop-types';
import { useState, useContext, useRef } from 'react';
import {
  getFormattedDateFromTimestamp,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import EmptyBroadcast from './EmptyBroadcast';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { API } from '../../../services';
import BroadcastDayLogCard from './BroadcastDayLogCard';
import useApiPolling from '../../../hooks/useApiPolling';

const { Title, Text } = Typography;

const BroadcastStatus = ({ setActiveKey, activeKey, setLastUpdatedTime }) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [broadcastDayLogData, setBroadcastDayLogData] = useState([]);
  const isFirstCall = useRef(true);

  const getBroadcastDayLog = async () => {
    try {
      const currentDate = getFormattedDateFromTimestamp(new Date());
      const { data } = await API.getBroadcastDayLog(currentDate);
      setBroadcastDayLogData(data);
      setLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstCall.current) {
        addErrorMessage(
          error?.response?.data?.Error ||
            "Unable to fetch today's broadcast log! Please try again after sometime."
        );
      }
    } finally {
      isFirstCall.current = false;
    }
  };

  useApiPolling(getBroadcastDayLog, activeKey === '1', [activeKey]);

  const sendRequest = async payload => {
    try {
      const { data, status } = await API.postBroadcastMessage(payload);
      if (status === 200) {
        addErrorMessage(data?.message, 'success');
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to broadcast the message! Please try again after sometime.'
        )
      );
    } finally {
      isFirstCall.current = false;
    }
  };

  const handleResendClick = item => {
    const payload = {
      targets: [{ device_id: item?.device_id, trip_id: item?.trip_id }],
      triggerfrequency: item?.trigger_frequency || 1,
      text_message: item?.text_message || item?.message,
      sent_by: sessionStorage.getItem('email'),
    };
    sendRequest(payload);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginTop: '16px',
            titleMarginBottom: '0px',
          },
          Card: {
            padding: 0,
            paddingLG: 0,
          },
          Table: {
            cellPaddingBlock: 12,
            colorBgContainer: 'var(--secondary-fill-color, #2E3033)',
            headerBg: 'var(--secondary-fill-color, #2E3033)',
            headerBorderRadius: 0,
            headerSplitColor: 'transparent',
          },
          Button: {
            paddingInline: 0,
            paddingBlock: 0,
          },
          List: {
            colorSplit: 'var(--primary-border-color, #71767C)',
          },
        },
        token: {
          fontSize: '1rem',
        },
      }}
    >
      {broadcastDayLogData?.length === 0 ? (
        <EmptyBroadcast />
      ) : (
        <Card variant="borderless" className="broadcast-status-card">
          <Flex gap={20} vertical>
            <div className="broadcast-status-title">
              <Title level={4} style={{ paddingTop: '16px' }}>
                Broadcast Status
              </Title>
              <Text className="broadcast-status-date">
                {getFormattedDateFromTimestamp(Date.now())}
              </Text>
            </div>
            {broadcastDayLogData?.length > 0 && (
              <List
                size="small"
                dataSource={broadcastDayLogData}
                split={false}
                renderItem={item => (
                  <>
                    <List.Item>
                      <BroadcastDayLogCard
                        data={item}
                        setActiveKey={setActiveKey}
                        handleResendClick={handleResendClick}
                      />
                    </List.Item>
                  </>
                )}
              />
            )}
          </Flex>
        </Card>
      )}
    </ConfigProvider>
  );
};

BroadcastStatus.propTypes = {
  setActiveKey: PropTypes.func.isRequired,
  activeKey: PropTypes.string,
  setLastUpdatedTime: PropTypes.func.isRequired,
};

export default BroadcastStatus;
